import React from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import {
  FieldArray,
  Field,
  reduxForm,
  change,
  getFormValues,
} from "redux-form";
import { History } from "history";
import _ from "lodash";
import { FormattedMessage } from "react-intl";

import IMyDelivery from "models/IMyDelivery";
import Input from "components/Input";
import Checkbox from "components/Checkbox";
import Select from "components/Select";
import BackgroundBlock from "../../partials/BackgroundBlock";
import ButtonsRow from "../../partials/ButtonsRow";
import CommentBlock from "../../partials/Comment";
import CNBDatePicker from "components/CNBDatePicker";
import { URLS } from "constants/urlConstants";
import Validations from "helpers/validations";
import { getFormatted, limitDecimals } from "helpers/formatting";
import { getTranslation } from "helpers/common";
import MyDeliveryActions from "../../actions";

import "./style.scss";

interface IProps {
  history: History;
  goNext: () => void;
  selectedDeliveries: IMyDelivery[] | any;
  formValues: any;
  changeField: (fieldname: string, value: any) => void;
  invalid: boolean;
  classOptions: any[];
  initFustLines: () => void;
  amStatusOptions: any[];
  lineTypes: any[];
}

interface IState {
  chainRegistered: boolean;
}

const DisabledInput = ({ label = "", value = "" as string | number }) => (
  <div className="cnb-input form-field">
    {label && (
      <label>
        <FormattedMessage id={label} />
      </label>
    )}
    <input value={value} disabled />
  </div>
);

const required = [Validations.required];

const fustValidation = (value, allValues, __, name) => {
  if (
    _.get(allValues, name.split(".")[0] + "perFust") &&
    _.get(allValues, name.split(".")[0] + "restFust") &&
    value <= 1
  ) {
    return getTranslation("VALIDATION_MORE_THAN_ONE");
  } else if (
    (_.get(allValues, name.split(".")[0] + "perFust") ||
      _.get(allValues, name.split(".")[0] + "restFust")) &&
    value <= 0
  ) {
    return getTranslation("VALIDATION_MORE_THAN_ZERO");
  }
  return undefined;
};

const certificateValidations = (value, allValues) =>
  allValues.chainRegistered && !value ? "Verplicht" : undefined;

class StepOne extends React.Component<IProps, IState> {
  constructor(props) {
    super(props);

    this.state = {
      chainRegistered: false,
    };
  }

  public componentDidMount() {
    const { history, selectedDeliveries, formValues = {} } = this.props;

    if (!selectedDeliveries) {
      history.push(URLS.MY_DELIVERIES_URL);
    }

    this.setState({
      chainRegistered: _.get(formValues, "chainRegistered", false),
    });
  }

  private toggleCheckbox = (chainRegistered) => {
    this.setState(
      {
        chainRegistered,
      },
      () => this.props.changeField("chainRegistered", chainRegistered)
    );
  };

  private copyLine = (fields, index) => {
    const newItem = _.pick(fields.get(index), [
      "id",
      "itemId",
      "harvestDate",
      "code",
      "price",
      "maat",
      "cultivar",
      "orderNummer",
      "bio",
      "quantityUnit",
      "quantity",
      "priceUnit",
      "class",
      "status",
      "comment",
      "sizeId",
      "growerClaims",
      "quantityUnitId",
      "totalDelivered",
    ]);
    fields.splice(index + 1, 0, newItem);
  };

  private calculateTotal = (item, value, allValues) => {
    const getValue = (key) => _.toNumber(_.get(allValues, key));
    const fust = _.get(allValues, `${item}.fust`, 0);
    const perFust = getValue(`${item}.perFust`);
    const restFust = getValue(`${item}.restFust`);
    const totalDelivered = _.get(allValues, `${item}.totalDelivered`, 0);
    const quantity = _.get(allValues, `${item}.quantity`, 0);
    const total = restFust ? (fust - 1) * perFust + restFust : fust * perFust;
    const deliveredPercent = (total + totalDelivered) / quantity;

    if (!_.isNaN(total)) {
      this.props.changeField(`${item}.total`, limitDecimals(total));
      this.props.changeField(
        `${item}.party`,
        _.find(this.props.lineTypes, [
          "value",
          deliveredPercent > 0.8
            ? totalDelivered > 0
              ? "RestDelivery"
              : "EntireDelivery"
            : "PartDelivery",
        ])
      );
    }

    return value;
  };

  private onNext = () => {
    this.props.initFustLines();
    this.props.goNext();
  };

  public render() {
    const { chainRegistered } = this.state;

    return (
      <div id="step-one">
        <BackgroundBlock>
          <div className="step-one-block">
            <Field
              name="buyer.Name"
              label="DELIVERIES_BUYER"
              component={Input}
              disabled
            />
            <Field
              name="date"
              label="DELIVERIES_DATE"
              component={CNBDatePicker}
              containerClassName="step-one-datepicker"
            />
            <div className="checkbox-row">
              <Checkbox
                checked={chainRegistered}
                onChange={this.toggleCheckbox}
              />
              <label className="checkbox-label">
                <FormattedMessage id="DELIVERIES_REPORT" />
              </label>
            </div>
          </div>
        </BackgroundBlock>
        <FieldArray name="orderLines" component={this.renderOrderLines} />
        <ButtonsRow goNext={this.onNext} disabled={this.props.invalid} />
      </div>
    );
  }

  private renderOrderLines = ({ fields }) => {
    const { formValues, classOptions, amStatusOptions, lineTypes } = this.props;

    return (
      <div>
        {fields.map((item, index) => (
          <BackgroundBlock
            key={index}
            showCloseBtn={fields.length > 1}
            showCopyBtn
            removeBlock={() => fields.remove(index)}
            copyBlock={() => this.copyLine(fields, index)}
          >
            <div className="step-one-row-wrapper">
              <div className="step-one-row first-row">
                <Field
                  containerClassName="form-field"
                  name={`${item}.fust`}
                  label="DELIVERIES_FUST"
                  component={Input}
                  normalize={(value, __, allValues) =>
                    this.calculateTotal(item, value, allValues)
                  }
                  parse={(value) => (value && parseInt(value, 10)) || 0}
                  validate={[
                    Validations.required,
                    Validations.positive,
                    fustValidation,
                  ]}
                />
                <Field
                  containerClassName="form-field"
                  name={`${item}.perFust`}
                  label="DELIVERIES_PER_FUST"
                  component={Input}
                  normalize={(value, __, allValues) =>
                    this.calculateTotal(item, value, allValues)
                  }
                  parse={limitDecimals}
                  validate={[Validations.required, Validations.positive]}
                />
                <Field
                  containerClassName="form-field"
                  name={`${item}.restFust`}
                  label="DELIVERIES_REST_FUST"
                  component={Input}
                  normalize={(value, __, allValues) =>
                    this.calculateTotal(item, value, allValues)
                  }
                  parse={limitDecimals}
                  validate={[Validations.positive]}
                />
                <DisabledInput
                  label="DELIVERIES_TOTAL"
                  value={getFormatted(
                    _.get(formValues, `${item}.total`),
                    "number"
                  )}
                />
                <Field
                  containerClassName="form-field"
                  className="min-width-select-forms"
                  name={`${item}.class`}
                  label="DELIVERIES_CLASS"
                  component={Select}
                  options={classOptions}
                  validate={required}
                />
                <Field
                  containerClassName="form-field"
                  className="min-width-select-forms"
                  name={`${item}.status`}
                  label="DELIVERIES_AM"
                  component={Select}
                  options={amStatusOptions}
                  validate={required}
                />
                <Field
                  containerClassName="form-field"
                  name={`${item}.certificate`}
                  label="DELIVERIES_BKD"
                  component={Input}
                  validate={[
                    Validations.exactLength(6),
                    Validations.onlyNumbers,
                    certificateValidations,
                  ]}
                />
                <Field
                  containerClassName="form-field"
                  className="min-width-select-forms"
                  name={`${item}.party`}
                  label="DELIVERIES_BATCH"
                  component={Select}
                  options={lineTypes}
                  validate={required}
                />
              </div>
              <div className="step-one-row second-row">
                <DisabledInput
                  label="ORDERS_NUMBER"
                  value={_.get(formValues, `${item}.orderNummer.label`)}
                />
                <DisabledInput
                  label="DELIVERIES_CULTIVAR"
                  value={_.get(formValues, `${item}.cultivar`)}
                />
                <DisabledInput
                  label="SIZE"
                  value={_.get(formValues, `${item}.maat`)}
                />
                <DisabledInput
                  label="PRICE"
                  value={
                    formValues.roleId === "Supplier"
                      ? ""
                      : getFormatted(
                          _.get(formValues, `${item}.price`),
                          "number"
                        )
                  }
                />
                <DisabledInput
                  label="PRICE_UNIT"
                  value={
                    formValues.roleId === "Supplier"
                      ? ""
                      : _.get(formValues, `${item}.priceUnit`)
                  }
                />
                <DisabledInput
                  label="DELIVERIES_QUANTITY"
                  value={getFormatted(
                    _.get(formValues, `${item}.quantity`),
                    "number"
                  )}
                />
                <DisabledInput
                  label="QUANTITY_UNIT"
                  value={_.get(formValues, `${item}.quantityUnit`)}
                />
                <DisabledInput
                  label="DELIVERIED_QUANTITY"
                  value={getFormatted(
                    _.get(formValues, `${item}.totalDelivered`, 0),
                    "number"
                  )}
                />
                <DisabledInput
                  label="BIO"
                  value={getFormatted(
                    _.get(formValues, `${item}.bio`),
                    "boolean"
                  )}
                />
              </div>
            </div>
            <Field
              name={`${item}.comment`}
              component={CommentBlock}
              maxLength={80}
            />
          </BackgroundBlock>
        ))}
      </div>
    );
  };
}

const StepOneWithReducForm = reduxForm({
  form: "delivery",
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
})(StepOne);

export default withRouter(
  connect(
    (state) => ({
      selectedDeliveries: state.myDeliveries.selectedDeliveries,
      formValues: getFormValues("delivery")(state),
      classOptions: state.preferences.classOptions,
      amStatusOptions: state.preferences.amStatusOptions,
      lineTypes: state.preferences.lineTypes,
    }),
    {
      changeField: (fieldName, value) => change("delivery", fieldName, value),
      initFustLines: MyDeliveryActions.initFustLines,
    }
  )(StepOneWithReducForm)
);

// withRouter(connect(mapStateToProps, mapDispatchToAction)(App));
